/**
 * Path resolution: find which centerline contains given node(s).
 *
 * Since nodes are shared across paths (LEFT and RIGHT share aortic nodes),
 * path resolution is done at computation time rather than declared in templates.
 *
 * Uses Centerline.getIndexByNodeId() for O(1) lookup per centerline.
 */

import type { Centerline } from '../../centerline';
import type { Vec3 } from '../../types';

/**
 * Find the centerline that contains both node IDs.
 *
 * For nodes in the shared aortic segment, either path works (returns first match).
 * For nodes spanning the bifurcation into different legs, only one path contains both.
 *
 * @returns The centerline and the indexes of both nodes within it, or null if no path contains both.
 */
export function findPathContainingNodes(
    nodeIdA: number,
    nodeIdB: number,
    centerlines: ReadonlyMap<string, Centerline>
): { centerline: Centerline; indexA: number; indexB: number } | null {
    for (const [, centerline] of centerlines) {
        const indexA = centerline.getIndexByNodeId(nodeIdA);
        const indexB = centerline.getIndexByNodeId(nodeIdB);
        if (indexA >= 0 && indexB >= 0) {
            return { centerline, indexA, indexB };
        }
    }
    return null;
}

/**
 * Collect centerline points between two nodes (inclusive), following the path
 * that contains both. Uses the same path resolution as `computeLength`, so the
 * returned trace matches a committed length's geometry.
 *
 * @returns Ordered world points along the path, or [] when no single path
 *          contains both nodes.
 */
export function collectPathPoints(
    fromNodeId: number,
    toNodeId: number,
    centerlines: ReadonlyMap<string, Centerline>
): Vec3[] {
    const resolved = findPathContainingNodes(fromNodeId, toNodeId, centerlines);
    if (!resolved) return [];

    const { centerline, indexA, indexB } = resolved;
    const startIdx = Math.min(indexA, indexB);
    const endIdx = Math.max(indexA, indexB);

    const points: Vec3[] = [];
    for (let i = startIdx; i <= endIdx; i++) {
        points.push(centerline.getNodeAt(i).point);
    }
    return points;
}

/**
 * Find a centerline containing the given node ID and return the node's index within it.
 *
 * @returns The first matching centerline and the node's index, or null.
 */
export function findPathContainingNode(
    nodeId: number,
    centerlines: ReadonlyMap<string, Centerline>
): { centerline: Centerline; index: number } | null {
    for (const [, centerline] of centerlines) {
        const index = centerline.getIndexByNodeId(nodeId);
        if (index >= 0) {
            return { centerline, index };
        }
    }
    return null;
}
